import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { leadsAPI, activitiesAPI, followUpsAPI, propertiesAPI, integrationsAPI, authAPI, teamAPI } from '@/lib/api';
import { validateName, validatePhone } from '@/lib/validation';
import { getActivityIcon } from '@/lib/activity-types';
import {
  ArrowLeft, Phone, Mail, Calendar, Building2, Clock,
  Plus, Edit2, Save, X, MessageSquare, Eye, FileText, Brain, Sparkles, Loader2, Send, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useSettings } from '@/contexts/SettingsContext';

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [linkedProps, setLinkedProps] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [agents, setAgents] = useState([]);
  const { settings, stageMap, formatCurrency, formatDate, formatDateTime } = useSettings();
  
  // Get sources list
  const SOURCES = ['website', 'referral', 'social_media', 'walk_in', 'phone_call', 'email', 'other'];
  
  // Title case helper
  const toTitleCase = (str) => {
    if (!str) return str;
    return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };
  
  // Get back path from location state or default to pipeline
  const backPath = location.state?.from || '/pipeline';
  
  // Get lead IDs array for prev/next navigation
  const leadIds = location.state?.leadIds || [];
  const currentIndex = leadIds.indexOf(id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < leadIds.length - 1;
  const prevLeadId = hasPrev ? leadIds[currentIndex - 1] : null;
  const nextLeadId = hasNext ? leadIds[currentIndex + 1] : null;
  
  const navigateToLead = (targetId) => {
    navigate(`/leads/${targetId}`, { 
      state: { 
        from: backPath,
        leadIds: leadIds 
      } 
    });
  };

  const fetchLead = useCallback(async () => {
    try {
      const res = await leadsAPI.get(id);
      setLead(res.data);
      setEditForm(res.data);
    } catch { navigate(backPath); }
  }, [id, navigate, backPath]);

  const fetchActivities = useCallback(async () => {
    const res = await activitiesAPI.list({ lead_id: id, limit: 50 });
    setActivities(res.data.activities);
  }, [id]);

  const fetchFollowUps = useCallback(async () => {
    const res = await followUpsAPI.list({ lead_id: id });
    setFollowUps(res.data.follow_ups);
  }, [id]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await teamAPI.members();
      console.log('Fetched agents for lead detail:', res.data);
      setAgents(res.data || []);
    } catch (err) {
      console.error('Failed to fetch agents for lead detail:', err);
    }
  }, []);

  useEffect(() => {
    fetchLead();
    fetchActivities();
    fetchFollowUps();
    fetchAgents();
  }, [fetchLead, fetchActivities, fetchFollowUps, fetchAgents]);

  const handleSave = async () => {
    const errs = {};
    const nameErr = validateName(editForm.name, 'Name', { min: 2, max: 100 });
    if (nameErr) errs.name = nameErr;
    if (editForm.email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(editForm.email.trim())) errs.email = 'Invalid email format';
    const phoneErr = validatePhone(editForm.phone);
    if (phoneErr) errs.phone = phoneErr;
    if (editForm.budget_min != null && editForm.budget_min < 0) errs.budget_min = 'Must be positive';
    if (editForm.budget_max != null && editForm.budget_max < 0) errs.budget_max = 'Must be positive';
    if (editForm.budget_min != null && editForm.budget_max != null && editForm.budget_max < editForm.budget_min) errs.budget_max = 'Must be >= min';

    setEditErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      return;
    }

    try {
      await leadsAPI.update(id, {
        ...editForm,
        name: editForm.name?.trim(),
        email: editForm.email?.trim().toLowerCase() || null,
        phone: editForm.phone?.trim() || null,
      });
      toast.success('Lead updated');
      setEditing(false);
      setEditErrors({});
      fetchLead();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : (typeof detail === 'string' ? detail : 'Update failed'));
    }
  };

  const handleStageChange = async (stage) => {
    try {
      await leadsAPI.updateStage(id, stage);
      toast.success('Stage updated');
      fetchLead();
    } catch { toast.error('Failed'); }
  };

  if (!lead) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="lead-detail-page">
      {/* Header with Navigation */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          
          {/* Prev/Next Navigation */}
          {leadIds.length > 0 && (
            <div className="flex items-center gap-1 ml-2 border-l pl-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateToLead(prevLeadId)}
                disabled={!hasPrev}
                className="h-8 w-8 p-0"
                title="Previous lead"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-2">
                {currentIndex + 1} / {leadIds.length}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateToLead(nextLeadId)}
                disabled={!hasNext}
                className="h-8 w-8 p-0"
                title="Next lead"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-primary/10 flex items-center justify-center text-base sm:text-xl font-bold text-primary flex-shrink-0">
            {lead.name?.charAt(0)}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold tracking-tight truncate">{lead.name}</h1>
            <div className="flex items-center gap-2 sm:gap-3 mt-0.5 sm:mt-1 flex-wrap">
              {lead.email && <span className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
              {lead.phone && <span className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={lead.stage || ""} onChange={(e) => handleStageChange(e.target.value)} data-testid="lead-stage-dropdown" className="flex h-9 w-[140px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            {(settings.pipeline_stages || []).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          {editing ? (
            <>
              <Button size="sm" onClick={handleSave} data-testid="save-lead-edit" className="gap-1">
                <Save className="w-4 h-4" />Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditForm(lead); setEditErrors({}); }} className="gap-1">
                <X className="w-4 h-4" />Cancel
              </Button>
              <span className="text-xs text-muted-foreground hidden sm:inline">💡 Save button also at bottom</span>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-lead-btn"><Edit2 className="w-4 h-4 mr-1" />Bewerken</Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Details */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {editing ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input value={editForm.name || ''} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))} className={editErrors.name ? 'border-destructive' : ''} />
                    {editErrors.name && <p className="text-[10px] text-destructive mt-0.5">{editErrors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input value={editForm.email || ''} onChange={(e) => setEditForm(p => ({...p, email: e.target.value}))} className={editErrors.email ? 'border-destructive' : ''} />
                    {editErrors.email && <p className="text-[10px] text-destructive mt-0.5">{editErrors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={editForm.phone || ''} onChange={(e) => setEditForm(p => ({...p, phone: e.target.value}))} placeholder="+14155551234" className={editErrors.phone ? 'border-destructive' : ''} />
                    {editErrors.phone && <p className="text-[10px] text-destructive mt-0.5">{editErrors.phone}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Source</Label>
                    <select 
                      value={editForm.source || 'website'} 
                      onChange={(e) => setEditForm(p => ({...p, source: e.target.value}))} 
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                    >
                      {SOURCES.map(s => <option key={s} value={s}>{toTitleCase(s)}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Property Interest</Label>
                    <Input value={editForm.property_interest || ''} onChange={(e) => setEditForm(p => ({...p, property_interest: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs">Timeline</Label>
                    <Input value={editForm.timeline || ''} onChange={(e) => setEditForm(p => ({...p, timeline: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs">Budget Min</Label>
                    <Input type="number" min="0" value={editForm.budget_min || ''} onChange={(e) => setEditForm(p => ({...p, budget_min: parseFloat(e.target.value) || null}))} className={editErrors.budget_min ? 'border-destructive' : ''} />
                    {editErrors.budget_min && <p className="text-[10px] text-destructive mt-0.5">{editErrors.budget_min}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Budget Max</Label>
                    <Input type="number" min="0" value={editForm.budget_max || ''} onChange={(e) => setEditForm(p => ({...p, budget_max: parseFloat(e.target.value) || null}))} className={editErrors.budget_max ? 'border-destructive' : ''} />
                    {editErrors.budget_max && <p className="text-[10px] text-destructive mt-0.5">{editErrors.budget_max}</p>}
                  </div>
                  
                  {/* Assign To Field */}
                  <div>
                    <Label className="text-xs">Assigned To</Label>
                    <select 
                      value={editForm.assigned_to || ''} 
                      onChange={(e) => setEditForm(p => ({...p, assigned_to: e.target.value}))} 
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                    >
                      <option value="">Unassigned</option>
                      {agents.map(agent => (
                        <option key={agent.user_id} value={agent.user_id}>
                          {agent.name} ({agent.role})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Custom Fields Edit */}
                  {settings.custom_fields_leads && settings.custom_fields_leads.length > 0 && (
                    <>
                      {settings.custom_fields_leads.map((field) => (
                        <div key={field.key}>
                          <Label className="text-xs">{field.label} {field.required && <span className="text-destructive">*</span>}</Label>
                          {field.type === 'text' && (
                            <Input 
                              value={editForm.custom_fields?.[field.key] || ''} 
                              onChange={(e) => setEditForm(p => ({
                                ...p, 
                                custom_fields: {...(p.custom_fields || {}), [field.key]: e.target.value}
                              }))} 
                            />
                          )}
                          {field.type === 'textarea' && (
                            <Textarea 
                              value={editForm.custom_fields?.[field.key] || ''} 
                              onChange={(e) => setEditForm(p => ({
                                ...p, 
                                custom_fields: {...(p.custom_fields || {}), [field.key]: e.target.value}
                              }))} 
                              rows={2}
                            />
                          )}
                          {field.type === 'number' && (
                            <Input 
                              type="number"
                              value={editForm.custom_fields?.[field.key] || ''} 
                              onChange={(e) => setEditForm(p => ({
                                ...p, 
                                custom_fields: {...(p.custom_fields || {}), [field.key]: parseFloat(e.target.value) || ''}
                              }))} 
                            />
                          )}
                          {field.type === 'select' && (
                            <select
                              value={editForm.custom_fields?.[field.key] || ''}
                              onChange={(e) => setEditForm(p => ({
                                ...p, 
                                custom_fields: {...(p.custom_fields || {}), [field.key]: e.target.value}
                              }))}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                            >
                              <option value="">Select...</option>
                              {field.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          )}
                          {field.type === 'checkbox' && (
                            <div className="flex items-center gap-2 h-9">
                              <input
                                type="checkbox"
                                checked={editForm.custom_fields?.[field.key] || false}
                                onChange={(e) => setEditForm(p => ({
                                  ...p, 
                                  custom_fields: {...(p.custom_fields || {}), [field.key]: e.target.checked}
                                }))}
                                className="w-4 h-4"
                              />
                            </div>
                          )}
                          {field.type === 'date' && (
                            <Input 
                              type="date"
                              value={editForm.custom_fields?.[field.key] || ''} 
                              onChange={(e) => setEditForm(p => ({
                                ...p, 
                                custom_fields: {...(p.custom_fields || {}), [field.key]: e.target.value}
                              }))} 
                            />
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  
                  <div><Label className="text-xs">Notes</Label><Textarea value={editForm.notes || ''} onChange={(e) => setEditForm(p => ({...p, notes: e.target.value}))} rows={3} maxLength={2000} /></div>
                  
                  {/* Save Actions - Sticky at bottom when editing */}
                  <div className="col-span-2 flex items-center justify-end gap-2 pt-4 border-t sticky bottom-0 bg-card/95 backdrop-blur-sm pb-2 -mb-2 z-10">
                    <Button 
                      variant="outline" 
                      onClick={() => { 
                        setEditing(false); 
                        setEditForm(lead); 
                        setEditErrors({}); 
                      }}
                      className="gap-1"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSave} 
                      data-testid="save-lead-edit-bottom"
                      className="gap-1"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <InfoRow label="Source" value={toTitleCase(lead.source)} />
                  <InfoRow label="Interest" value={lead.property_interest} />
                  <InfoRow label="Timeline" value={lead.timeline} />
                  <InfoRow label="Budget" value={lead.budget_min || lead.budget_max ? `${formatCurrency(lead.budget_min||0)} - ${formatCurrency(lead.budget_max||0)}` : '—'} />
                  <InfoRow label="Assigned To" value={lead.assigned_to_name || '—'} />
                  <InfoRow label="Created" value={formatDate(lead.created_at)} />
                  
                  {/* Custom Fields Display */}
                  {settings.custom_fields_leads && settings.custom_fields_leads.length > 0 && (
                    <>
                      {settings.custom_fields_leads.map((field) => {
                        const value = lead.custom_fields?.[field.key];
                        if (!value && value !== 0 && value !== false) return null;
                        
                        let displayValue = value;
                        if (field.type === 'select' || field.type === 'radio') {
                          displayValue = value;
                        } else if (field.type === 'checkbox') {
                          displayValue = value ? 'Yes' : 'No';
                        } else if (field.type === 'date') {
                          displayValue = formatDate(value);
                        }
                        
                        return <InfoRow key={field.key} label={field.label} value={displayValue} />;
                      })}
                    </>
                  )}
                  
                  {lead.notes && <div className="pt-2 border-t"><p className="text-xs text-muted-foreground">{lead.notes}</p></div>}
                </>
              )}
            </CardContent>
          </Card>

          {/* AI Score */}
          {lead.ai_score && (
            <Card className="border-border/60 border-primary/20 bg-primary/5" data-testid="ai-score-card">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">AI Score</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${
                      lead.ai_grade === 'A' ? 'text-green-600' :
                      lead.ai_grade === 'B' ? 'text-blue-600' :
                      lead.ai_grade === 'C' ? 'text-amber-600' : 'text-red-600'
                    }`}>{lead.ai_grade}</span>
                    <span className="text-sm text-muted-foreground">{lead.ai_score}/100</span>
                  </div>
                </div>
                {lead.ai_summary && <p className="text-xs text-muted-foreground leading-relaxed">{lead.ai_summary}</p>}
                {lead.ai_next_action && (
                  <div className="bg-background rounded-md p-2 border">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase">Recommended Action</p>
                    <p className="text-xs mt-0.5">{lead.ai_next_action}</p>
                  </div>
                )}
                <p className="text-[9px] text-muted-foreground">Scored with {lead.ai_model_used} on {formatDate(lead.ai_scored_at)}</p>
              </CardContent>
            </Card>
          )}

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-border/60">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{lead.activity_count || 0}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Activities</p>
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{lead.property_count || 0}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Properties</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right - Timeline */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="timeline">
            <TabsList>
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="follow-ups" data-testid="tab-follow-ups">Follow-ups ({followUps.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline" className="space-y-4 mt-4">
              <div className="flex justify-end gap-1.5 sm:gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setShowSendMessage('sms')} data-testid="send-sms-btn" className="gap-1.5 text-blue-600 border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30">
                  <Phone className="w-3.5 h-3.5" /> SMS
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowSendMessage('whatsapp')} data-testid="send-whatsapp-btn" className="gap-1.5 text-green-600 border-green-200 dark:border-green-900 hover:bg-green-50 dark:hover:bg-green-950/30">
                  <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowSendMessage('email')} data-testid="send-email-btn" className="gap-1.5 text-indigo-600 border-indigo-200 dark:border-indigo-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/30">
                  <Mail className="w-3.5 h-3.5" /> Email
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddActivity(true)} data-testid="add-activity-btn" className="gap-2">
                  <Plus className="w-4 h-4" /> Log Activity
                </Button>
              </div>
              <div className="space-y-0">
                {activities.map((act) => {
                  const Icon = getActivityIcon(act.type);
                  return (
                    <div key={act.id} className="flex gap-4 py-3 border-b border-border/40 last:border-0">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-muted-foreground" /></div>
                        <div className="flex-1 w-px bg-border/40 mt-2" />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium capitalize">{act.type}</p>
                          <span className="text-[10px] mono text-muted-foreground">{formatDateTime(act.created_at)}</span>
                        </div>
                        {act.outcome && <Badge variant="secondary" className="text-[10px] mt-1">{act.outcome.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>}
                        {act.notes && <p className="text-xs text-muted-foreground mt-1">{act.notes}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">by {act.created_by_name}</p>
                      </div>
                    </div>
                  );
                })}
                {activities.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No activities logged yet</p>}
              </div>
            </TabsContent>

            <TabsContent value="follow-ups" className="space-y-4 mt-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowAddFollowUp(true)} data-testid="add-followup-btn" className="gap-2">
                  <Plus className="w-4 h-4" /> Schedule Follow-up
                </Button>
              </div>
              {followUps.map((fu) => {
                const isOverdue = new Date(fu.due_date) < new Date() && fu.status === 'pending';
                return (
                  <Card key={fu.id} className={`border-border/60 ${isOverdue ? 'border-red-300 dark:border-red-900' : ''}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className={`w-4 h-4 ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`} />
                        <div>
                          <p className="text-sm font-medium capitalize">{fu.type}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(fu.due_date)} {fu.notes && `- ${fu.notes}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={fu.status === 'completed' ? 'default' : isOverdue ? 'destructive' : 'secondary'} className="text-[10px]">
                          {isOverdue ? 'Overdue' : fu.status}
                        </Badge>
                        {fu.status === 'pending' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                            await followUpsAPI.update(fu.id, { status: 'completed' });
                            toast.success('Marked as done');
                            fetchFollowUps();
                          }} data-testid={`complete-followup-${fu.id}`}>Done</Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {followUps.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No follow-ups scheduled</p>}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Activity Dialog */}
      <AddActivityDialog open={showAddActivity} onClose={() => setShowAddActivity(false)} leadId={id} onSuccess={() => { fetchActivities(); fetchLead(); }} />
      <AddFollowUpDialog open={showAddFollowUp} onClose={() => setShowAddFollowUp(false)} leadId={id} onSuccess={fetchFollowUps} />
      <SendMessageDialog open={!!showSendMessage} channel={showSendMessage} onClose={() => setShowSendMessage(null)} lead={lead} onSuccess={() => { fetchActivities(); }} />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium capitalize">{value || '—'}</span>
    </div>
  );
}

function AddActivityDialog({ open, onClose, leadId, onSuccess }) {
  const [type, setType] = useState('call');
  const [outcome, setOutcome] = useState('positive');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const activityTypes = (settings.activity_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });
  const outcomes = settings.activity_outcomes || ['positive', 'neutral', 'negative', 'no_answer', 'callback_requested'];
  
  // Title case helper
  const toTitleCase = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await activitiesAPI.create({ lead_id: leadId, type, outcome, notes });
      toast.success('Activity logged');
      onSuccess();
      onClose();
      setType('call'); setOutcome('positive'); setNotes('');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-activity-dialog">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              {activityTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Outcome</Label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              {outcomes.map(o => <option key={o} value={o}>{toTitleCase(o)}</option>)}
            </select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-activity-btn">{saving ? 'Saving...' : 'Log Activity'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddFollowUpDialog({ open, onClose, leadId, onSuccess }) {
  const [type, setType] = useState('call');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const followUpTypes = (settings.follow_up_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await followUpsAPI.create({ lead_id: leadId, type, due_date: new Date(dueDate).toISOString(), notes });
      toast.success('Follow-up scheduled');
      onSuccess();
      onClose();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-followup-dialog">
        <DialogHeader><DialogTitle>Schedule Follow-up</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Type</Label>
            <select value={type || ""} onChange={(e) => setType(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
{followUpTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
</select>
          </div>
          <div><Label>Due Date</Label><Input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required data-testid="followup-date-input" /></div>
          <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !dueDate} data-testid="save-followup-btn">{saving ? 'Saving...' : 'Schedule'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function SendMessageDialog({ open, channel, onClose, lead, onSuccess }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [toValue, setToValue] = useState('');
  const [sending, setSending] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const isEmail = channel === 'email';

  useEffect(() => {
    if (open && lead) {
      setToValue(isEmail ? (lead.email || '') : (lead.phone || ''));
      setSubject(''); setMessage(''); setSelectedTemplate('');
      if (isEmail) {
        integrationsAPI.getEmailConfig().then(res => {
          setTemplates(res.data.templates || []);
          setConfigLoaded(!!res.data.email_enabled);
        }).catch(() => setConfigLoaded(false));
      } else {
        integrationsAPI.get().then(res => {
          const key = channel === 'sms' ? 'sms_templates' : 'whatsapp_templates';
          setTemplates(res.data[key] || []);
          setConfigLoaded(channel === 'sms' ? res.data.sms_enabled : res.data.whatsapp_enabled);
        }).catch(() => setConfigLoaded(false));
      }
    }
  }, [open, lead, channel, isEmail]);

  const handleTemplateSelect = (tmplId) => {
    setSelectedTemplate(tmplId);
    const tmpl = templates.find(t => t.id === tmplId);
    if (tmpl) { setMessage(tmpl.body); if (tmpl.subject) setSubject(tmpl.subject); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!toValue || !message) return;
    setSending(true);
    try {
      if (isEmail) {
        await integrationsAPI.sendEmail({ lead_id: lead.id, to_email: toValue, subject, body: message });
      } else {
        const api = channel === 'sms' ? integrationsAPI.sendSMS : integrationsAPI.sendWhatsApp;
        await api({ lead_id: lead.id, to_number: toValue, message });
      }
      toast.success(`${channel === 'sms' ? 'SMS' : channel === 'whatsapp' ? 'WhatsApp' : 'Email'} sent`);
      onSuccess(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const icons = { sms: Phone, whatsapp: MessageSquare, email: Mail };
  const colors = { sms: 'text-blue-500', whatsapp: 'text-green-500', email: 'text-indigo-500' };
  const labels = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };
  const Icon = icons[channel] || Phone;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="send-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${colors[channel]}`} />
            Send {labels[channel]} to {lead?.name}
          </DialogTitle>
        </DialogHeader>
        {!configLoaded ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>{labels[channel]} integration is not enabled.</p>
            <p className="text-xs mt-1">Ask your admin to configure it in Integrations.</p>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <Label className="text-xs">{isEmail ? 'To Email' : 'To Number'}</Label>
              <Input value={toValue} onChange={(e) => setToValue(e.target.value)} placeholder={isEmail ? 'lead@example.com' : '+14155551234'} className={`text-xs ${isEmail ? '' : 'mono'}`} required data-testid="msg-to-value" />
            </div>
            {templates.length > 0 && (
              <div>
                <Label className="text-xs">Template (optional)</Label>
                <select value={selectedTemplate || ""} onChange={(e) => handleTemplateSelect(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
</select>
              </div>
            )}
            {isEmail && (
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject..." className="text-xs" required data-testid="msg-subject" />
              </div>
            )}
            <div>
              <Label className="text-xs">{isEmail ? 'Body' : 'Message'}</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={isEmail ? 6 : 4} required data-testid="msg-body" />
              <p className="text-[9px] text-muted-foreground mt-1">Variables like {'{lead_name}'} will be auto-replaced</p>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={sending || !toValue || !message} data-testid="send-msg-btn" className="gap-1.5">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
